import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Brain, 
  Video, 
  Users, 
  Calendar, 
  Shield, 
  MessageCircle,
  Heart,
  Activity,
  BookOpen,
  Award,
  ChevronRight,
  Clock,
  Star,
  CheckCircle,
  ArrowRight,
  Phone,
  Globe,
  Sparkles
} from 'lucide-react';

/**
 * ServicesPage Component
 * 
 * Based on GitHub patterns from:
 * - Healthcare React applications (OpenMRS, Medplum)
 * - Mental health platforms (MindStrong, Talkspace)
 * - Responsive design patterns from Material-UI and Tailwind
 * 
 * Features:
 * - Tablet-optimized responsive grid layout (768px - 1024px)
 * - Animated service cards with framer-motion
 * - Interactive hover states
 * - Accessible design following WCAG guidelines
 * - Integration with existing Rhythm of life routing structure
 */

const ServicesPage = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [hoveredService, setHoveredService] = useState(null);

  // Service categories for filtering
  const categories = [
    { id: 'all', label: 'All Services', icon: Sparkles },
    { id: 'therapy', label: 'Therapy', icon: MessageCircle },
    { id: 'assessment', label: 'Assessment', icon: Brain },
    { id: 'support', label: 'Support', icon: Heart },
    { id: 'wellness', label: 'Wellness', icon: Activity }
  ];

  // Comprehensive services data based on project structure
  const services = [
    {
      id: 'teletherapy',
      category: 'therapy',
      title: 'Online Therapy & Counseling',
      description: 'Connect with licensed therapists through secure video sessions from the comfort of your home.',
      icon: Video,
      color: 'from-serene-blue-400 to-serene-blue-600',
      features: [
        'Individual therapy sessions',
        'Couples counseling',
        'Family therapy',
        'Flexible scheduling'
      ],
      price: 'Starting from ₹1,500/session',
      duration: '50 minutes',
      availability: '24/7 booking',
      link: '/booking/find-provider'
    },
    {
      id: 'psychiatric',
      category: 'therapy',
      title: 'Psychiatric Consultation',
      description: 'Expert psychiatric evaluation and medication management with board-certified psychiatrists.',
      icon: Brain,
      color: 'from-calm-purple-400 to-calm-purple-600',
      features: [
        'Comprehensive evaluation',
        'Medication management',
        'Treatment planning',
        'Follow-up care'
      ],
      price: 'Starting from ₹2,500/session',
      duration: '45 minutes',
      availability: 'Mon-Sat',
      link: '/booking/find-provider'
    },
    {
      id: 'assessments',
      category: 'assessment',
      title: 'Clinical Assessments',
      description: 'Evidence-based mental health assessments including PHQ-9, GAD-7, and specialized evaluations.',
      icon: BookOpen,
      color: 'from-growth-green-400 to-growth-green-600',
      features: [
        'Depression screening',
        'Anxiety assessment',
        'ADHD evaluation',
        'Personality assessment'
      ],
      price: 'Free self-assessments',
      duration: '15-30 minutes',
      availability: 'Instant access',
      link: '/self-help'
    },
    {
      id: 'group-therapy',
      category: 'therapy',
      title: 'Group Therapy Sessions',
      description: 'Join supportive group sessions led by experienced facilitators for shared healing and growth.',
      icon: Users,
      color: 'from-orange-400 to-orange-600',
      features: [
        'Support groups',
        'Skill-building workshops',
        'Mindfulness sessions',
        'Peer support'
      ],
      price: 'Starting from ₹500/session',
      duration: '90 minutes',
      availability: 'Weekly sessions',
      link: '/booking/find-provider'
    },
    {
      id: 'crisis-support',
      category: 'support',
      title: '24/7 Crisis Support',
      description: 'Immediate support during mental health emergencies with trained crisis counselors.',
      icon: Phone,
      color: 'from-red-400 to-red-600',
      features: [
        'Emergency helpline',
        'Crisis intervention',
        'Safety planning',
        'Immediate response'
      ],
      price: 'Free service',
      duration: 'As needed',
      availability: '24/7',
      link: '/emergency'
    },
    {
      id: 'wellness-programs',
      category: 'wellness',
      title: 'Wellness & Prevention Programs',
      description: 'Holistic programs focusing on mental wellness, stress management, and preventive care.',
      icon: Activity,
      color: 'from-teal-400 to-cyan-600',
      features: [
        'Stress management',
        'Sleep therapy',
        'Mindfulness training',
        'Lifestyle coaching'
      ],
      price: 'Starting from ₹3,000/month',
      duration: '4-8 weeks',
      availability: 'Flexible',
      link: '/self-help'
    },
    {
      id: 'corporate-wellness',
      category: 'wellness',
      title: 'Corporate Mental Health',
      description: 'Comprehensive mental health solutions for organizations and their employees.',
      icon: Shield,
      color: 'from-indigo-400 to-indigo-600',
      features: [
        'Employee assistance programs',
        'Workplace workshops',
        'Manager training',
        'Organizational assessment'
      ],
      price: 'Custom pricing',
      duration: 'Customizable',
      availability: 'On-demand',
      link: '/contact'
    },
    {
      id: 'child-adolescent',
      category: 'therapy',
      title: 'Child & Adolescent Services',
      description: 'Specialized mental health services for children and teenagers with age-appropriate approaches.',
      icon: Heart,
      color: 'from-pink-400 to-pink-600',
      features: [
        'Play therapy',
        'Teen counseling',
        'Parent guidance',
        'School support'
      ],
      price: 'Starting from ₹1,800/session',
      duration: '45 minutes',
      availability: 'After school hours',
      link: '/booking/find-provider'
    }
  ];

  // Filter services based on active category
  const filteredServices = activeCategory === 'all' 
    ? services 
    : services.filter(service => service.category === activeCategory);

  // Animation variants for framer-motion
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  // Statistics section data
  const statistics = [
    { value: '10,000+', label: 'Patients Served', icon: Users },
    { value: '98%', label: 'Satisfaction Rate', icon: Star },
    { value: '50+', label: 'Expert Therapists', icon: Award },
    { value: '24/7', label: 'Support Available', icon: Clock }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-serene-blue-50 via-white to-calm-purple-50">
      {/* Hero Section */}
      <motion.section 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative py-12 md:py-16 lg:py-20 px-4 md:px-8 lg:px-12"
      >
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1 
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Comprehensive Mental Health Services
          </motion.h1>
          <motion.p 
            className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Evidence-based, compassionate care tailored to your unique needs. 
            Take the first step towards better mental health today.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <button
              onClick={() => navigate('/booking/find-provider')}
              className="px-8 py-3 bg-gradient-to-r from-serene-blue-500 to-serene-blue-600 text-white 
                       font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 
                       transition-all duration-300 flex items-center justify-center gap-2"
            >
              Book Consultation
              <ArrowRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigate('/self-help')}
              className="px-8 py-3 bg-white text-serene-blue-600 font-semibold rounded-full 
                       shadow-lg hover:shadow-xl transform hover:scale-105 transition-all 
                       duration-300 border-2 border-serene-blue-300"
            >
              Free Self-Assessment
            </button>
          </motion.div>
        </div>

        {/* Decorative Background Elements */}
        <div className="absolute top-0 left-0 w-32 h-32 bg-serene-blue-200 rounded-full 
                      filter blur-3xl opacity-30 -z-10"></div>
        <div className="absolute bottom-0 right-0 w-40 h-40 bg-calm-purple-200 rounded-full 
                      filter blur-3xl opacity-30 -z-10"></div>
      </motion.section>

      {/* Statistics Section */}
      <motion.section 
        className="py-8 md:py-12 px-4 md:px-8 lg:px-12 bg-white/80 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {statistics.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <stat.icon className="w-8 h-8 mx-auto mb-2 text-serene-blue-500" />
                <div className="text-2xl md:text-3xl font-bold text-gray-900">{stat.value}</div>
                <div className="text-sm md:text-base text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Category Filter - Tablet Optimized */}
      <section className="py-6 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-2 md:gap-3">
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <motion.button
                  key={category.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveCategory(category.id)}
                  className={`
                    px-4 md:px-6 py-2 md:py-3 rounded-full font-medium transition-all duration-300
                    flex items-center gap-2 text-sm md:text-base
                    ${activeCategory === category.id 
                      ? 'bg-gradient-to-r from-serene-blue-500 to-serene-blue-600 text-white shadow-lg' 
                      : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md'
                    }
                  `}
                >
                  <Icon className="w-4 h-4 md:w-5 md:h-5" />
                  {category.label}
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Grid - Tablet Optimized */}
      <motion.section 
        className="py-8 md:py-12 px-4 md:px-8 lg:px-12"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filteredServices.map((service) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.id}
                  variants={itemVariants}
                  whileHover={{ y: -8, transition: { duration: 0.3 } }}
                  onMouseEnter={() => setHoveredService(service.id)}
                  onMouseLeave={() => setHoveredService(null)}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 
                           overflow-hidden group cursor-pointer"
                  onClick={() => navigate(service.link)}
                >
                  {/* Service Card Header */}
                  <div className={`
                    h-2 bg-gradient-to-r ${service.color} 
                    group-hover:h-3 transition-all duration-300
                  `}></div>

                  <div className="p-6 md:p-8">
                    {/* Icon and Title */}
                    <div className="flex items-start gap-4 mb-4">
                      <div className={`
                        p-3 rounded-xl bg-gradient-to-br ${service.color} 
                        text-white shadow-md group-hover:shadow-lg transition-all duration-300
                      `}>
                        <Icon className="w-6 h-6 md:w-7 md:h-7" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1">
                          {service.title}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Clock className="w-3 h-3" />
                          <span>{service.duration}</span>
                          <span className="mx-1">•</span>
                          <span>{service.availability}</span>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 text-sm md:text-base mb-4 line-clamp-2">
                      {service.description}
                    </p>

                    {/* Features List */}
                    <div className="mb-4">
                      {service.features.slice(0, hoveredService === service.id ? 4 : 3).map((feature, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-center gap-2 text-sm text-gray-700 mb-2"
                        >
                          <CheckCircle className="w-4 h-4 text-growth-green-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </motion.div>
                      ))}
                    </div>

                    {/* Price and CTA */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div>
                        <p className="text-sm text-gray-500">Starting from</p>
                        <p className="font-bold text-lg text-gray-900">{service.price}</p>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={`
                          px-4 py-2 rounded-full bg-gradient-to-r ${service.color} 
                          text-white font-medium text-sm flex items-center gap-1
                          shadow-md hover:shadow-lg transition-all duration-300
                        `}
                      >
                        Learn More
                        <ChevronRight className="w-4 h-4" />
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.section>

      {/* How It Works Section */}
      <motion.section 
        className="py-12 md:py-16 px-4 md:px-8 lg:px-12 bg-white/90 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center text-gray-900 mb-8">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                step: '1',
                title: 'Choose Your Service',
                description: 'Browse our services and select the support that best fits your needs.',
                icon: Calendar
              },
              {
                step: '2',
                title: 'Book Appointment',
                description: 'Schedule at your convenience with our easy online booking system.',
                icon: Users
              },
              {
                step: '3',
                title: 'Start Your Journey',
                description: 'Connect with your provider and begin your path to mental wellness.',
                icon: Heart
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
              >
                <div className="relative">
                  <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 bg-gradient-to-br 
                                from-serene-blue-500 to-serene-blue-600 rounded-full 
                                flex items-center justify-center text-white font-bold text-xl md:text-2xl
                                shadow-lg">
                    {step.step}
                  </div>
                  {index < 2 && (
                    <div className="hidden md:block absolute top-10 left-full w-full h-0.5 
                                  bg-gradient-to-r from-serene-blue-300 to-transparent -z-10"></div>
                  )}
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-sm md:text-base text-gray-600">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Insurance and Payment Section */}
      <motion.section 
        className="py-12 md:py-16 px-4 md:px-8 lg:px-12"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            Insurance & Payment Options
          </h2>
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {['Major Insurance', 'Flexible Payment', 'EMI Available', 'Corporate Plans'].map((item, index) => (
                <div key={index} className="flex flex-col items-center">
                  <Shield className="w-8 h-8 md:w-10 md:h-10 text-serene-blue-500 mb-2" />
                  <span className="text-xs md:text-sm text-gray-700">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-gray-600 mb-4">
              We accept major insurance plans and offer flexible payment options to make mental health care accessible.
            </p>
            <button
              onClick={() => navigate('/contact')}
              className="px-6 py-2 bg-gradient-to-r from-serene-blue-500 to-serene-blue-600 
                       text-white font-medium rounded-full hover:shadow-lg transition-all duration-300"
            >
              Check Coverage
            </button>
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section 
        className="py-12 md:py-16 px-4 md:px-8 lg:px-12 bg-gradient-to-r from-serene-blue-600 to-calm-purple-600"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
            Ready to Start Your Mental Health Journey?
          </h2>
          <p className="text-lg md:text-xl mb-8 opacity-90">
            Take the first step towards a healthier, happier you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/booking/find-provider')}
              className="px-8 py-3 bg-white text-serene-blue-600 font-bold rounded-full 
                       shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Schedule Consultation
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/emergency')}
              className="px-8 py-3 bg-transparent text-white font-bold rounded-full 
                       border-2 border-white hover:bg-white hover:text-serene-blue-600 
                       transition-all duration-300"
            >
              Crisis Support
            </motion.button>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default ServicesPage;